/**
 * useRSSContent Hook
 *
 * Manages fetching RSS content items from Supabase.
 * Polls for updates every 10 seconds.
 */

import { useState, useEffect, useCallback } from "react";
import { fetchRSSContent, fetchRSSContentMultipleStatuses } from "api/supabase";

const POLL_INTERVAL = 10000; // 10 seconds

function useRSSContent(status = "New") {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setError(null);

      // If status is an array or includes multiple values, use the multiple statuses function
      let data;
      if (Array.isArray(status)) {
        data = await fetchRSSContentMultipleStatuses(status);
      } else if (status === "New") {
        // For RSS Content tab, fetch both New and Processing
        data = await fetchRSSContentMultipleStatuses(["New", "Processing"]);
      } else {
        data = await fetchRSSContent(status);
      }

      setItems(data);
    } catch (err) {
      console.error("Error fetching RSS content:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchItems();

    // Set up polling
    const interval = setInterval(fetchItems, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchItems]);

  const refetch = useCallback(() => {
    setLoading(true);
    fetchItems();
  }, [fetchItems]);

  return {
    items,
    loading,
    error,
    refetch,
  };
}

export default useRSSContent;
